import React, { useMemo } from 'react';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  DataGridNumberedPagination,
  DATA_GRID_DEFAULT_PAGE_SIZE,
  DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS,
} from '../../../../components/DataGridNumberedPagination';
import { resolveScheduleTahapanCaption } from '../../../../components/surveyScheduleCalendar/QuotationScheduleDayDetailDialog';
import type { QuotationScheduleDaySummary } from '../../../../components/surveyScheduleCalendar';
import { scheduleDayTitle } from '../../../../sampling/surveyScheduleCalendar';

export type SamplingScheduleGridRow = {
  id: string;
  date: string;
  summary: QuotationScheduleDaySummary;
};

export function buildSamplingScheduleGridRows(
  summariesByDate: Map<string, QuotationScheduleDaySummary[]>,
): SamplingScheduleGridRow[] {
  const rows: SamplingScheduleGridRow[] = [];
  for (const [date, summaries] of summariesByDate.entries()) {
    for (const summary of summaries) {
      rows.push({
        id: `${date}:${summary.scheduleDocumentId}`,
        date,
        summary,
      });
    }
  }
  rows.sort((a, b) => {
    const byDate = b.date.localeCompare(a.date);
    if (byDate !== 0) return byDate;
    return a.summary.quotationNumber.localeCompare(b.summary.quotationNumber, 'id');
  });
  return rows;
}

function detailSamplingHint(summary: QuotationScheduleDaySummary): string | null {
  const workOrderDocId = String(summary.workOrderDocumentId ?? '').trim();
  const jobNumber = String(summary.workOrderJobNumber ?? '').trim();
  if (workOrderDocId && jobNumber) return null;
  if (!workOrderDocId) return 'Work order belum dibuat untuk penjadwalan ini.';
  return 'Menunggu persetujuan admin sampling. Nomor job belum terbit.';
}

const cellStackSx = { py: 0.75, width: '100%', minWidth: 0 } as const;

export function SamplingScheduleGridList({ rows }: { rows: SamplingScheduleGridRow[] }) {
  const navigate = useNavigate();

  const columns: GridColDef<SamplingScheduleGridRow>[] = useMemo(
    () => [
      {
        field: 'date',
        headerName: 'Tanggal',
        minWidth: 170,
        flex: 0.7,
        valueGetter: (_, row) => row.date,
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ py: 0.75 }}>
            {scheduleDayTitle(row.date)}
          </Typography>
        ),
      },
      {
        field: 'quotationNumber',
        headerName: 'Quotation',
        minWidth: 170,
        flex: 0.8,
        sortable: false,
        valueGetter: (_, row) =>
          [row.summary.quotationNumber, row.summary.groupLabel].filter(Boolean).join(' '),
        renderCell: ({ row }) => (
          <Stack spacing={0.25} sx={cellStackSx}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.summary.quotationNumber || '—'}
            </Typography>
            {row.summary.groupLabel ? (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Group sample: {row.summary.groupLabel}
              </Typography>
            ) : null}
          </Stack>
        ),
      },
      {
        field: 'customerName',
        headerName: 'Pelanggan',
        minWidth: 200,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => row.summary.customerName || '—',
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ py: 0.75, whiteSpace: 'normal' }}>
            {row.summary.customerName || '—'}
          </Typography>
        ),
      },
      {
        field: 'workOrderJobNumber',
        headerName: 'Nomor job',
        minWidth: 130,
        flex: 0.55,
        sortable: false,
        valueGetter: (_, row) => String(row.summary.workOrderJobNumber ?? '').trim() || 'Belum terbit',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ py: 0.75 }}>
            {String(value ?? '')}
          </Typography>
        ),
      },
      {
        field: 'team',
        headerName: 'Tim survey',
        minWidth: 220,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) => {
          const members = row.summary.members
            .map(
              (member) =>
                `${member.name}${member.isLead ? ' (Lead)' : ''} ${member.timeLabels.join(', ') || 'Sepanjang hari'}`,
            )
            .join('; ');
          return `Lead: ${row.summary.teamLeadName} · Total: ${row.summary.teamCount} petugas. ${members}`;
        },
        renderCell: ({ row }) => (
          <Stack spacing={0.5} sx={cellStackSx}>
            <Typography variant="body2" color="text.secondary">
              Lead: {row.summary.teamLeadName} · Total: {row.summary.teamCount} petugas
            </Typography>
            {row.summary.members.map((member) => (
              <Box key={member.userId}>
                <Typography variant="body2">
                  {member.name}
                  {member.isLead ? ' (Lead)' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {member.timeLabels.join(', ') || 'Sepanjang hari'}
                </Typography>
              </Box>
            ))}
          </Stack>
        ),
      },
      {
        field: 'address',
        headerName: 'Alamat',
        minWidth: 280,
        flex: 1.4,
        sortable: false,
        valueGetter: (_, row) => row.summary.address || 'Alamat belum tersedia.',
        renderCell: ({ row }) => (
          <Typography
            variant="body2"
            color={row.summary.address ? 'text.primary' : 'text.secondary'}
            sx={{ py: 0.75, whiteSpace: 'normal' }}
          >
            {row.summary.address || 'Alamat belum tersedia.'}
          </Typography>
        ),
      },
      {
        field: 'tahapan',
        headerName: 'Tahapan',
        minWidth: 240,
        flex: 1,
        sortable: false,
        valueGetter: (_, row) =>
          resolveScheduleTahapanCaption(row.summary.scheduleStatus, false) ?? '—',
        renderCell: ({ row }) => {
          const caption = resolveScheduleTahapanCaption(row.summary.scheduleStatus, false);
          return (
            <Typography
              variant="body2"
              color={caption ? 'text.primary' : 'text.secondary'}
              sx={{ py: 0.75, whiteSpace: 'normal' }}
            >
              {caption ?? '—'}
            </Typography>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Aksi',
        width: 170,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => {
          const hint = detailSamplingHint(row.summary);
          const workOrderDocId = String(row.summary.workOrderDocumentId ?? '').trim();
          const jobNumber = String(row.summary.workOrderJobNumber ?? '').trim();
          return (
            <Box sx={{ py: 0.75 }}>
              <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
                <span>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={Boolean(hint)}
                    data-testid={`sampling-grid-open-detail-${row.id}`}
                    onClick={() => {
                      if (!workOrderDocId || !jobNumber) return;
                      navigate(`/sampling/sampling/${encodeURIComponent(workOrderDocId)}#detail`);
                    }}
                  >
                    Detail sampling
                  </Button>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [navigate],
  );

  return (
    <Box sx={{ width: '100%' }} data-testid="sampling-schedule-grid-list">
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        pageSizeOptions={[...DATA_GRID_NUMBERED_PAGE_SIZE_OPTIONS]}
        initialState={{
          pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } },
          sorting: { sortModel: [{ field: 'date', sort: 'desc' }] },
        }}
        slots={{ pagination: DataGridNumberedPagination }}
        autoHeight
        density="compact"
        sx={{
          '& .MuiDataGrid-columnHeaders': { fontWeight: 600 },
          '& .MuiDataGrid-cell': { alignItems: 'flex-start', py: 0.5, whiteSpace: 'normal', lineHeight: 1.4 },
        }}
      />
    </Box>
  );
}
